import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cache for reCAPTCHA settings
let settingsCache = null;
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Custom hook for Google reCAPTCHA v2 Invisible integration
 * 
 * @param {string} formType - One of 'signup', 'login', 'forgot_password'
 * @returns {Object} - { executeRecaptcha, isLoading, isEnabled, resetRecaptcha }
 */
export const useRecaptcha = (formType) => {
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const recaptchaId = useRef(null);
  const containerRef = useRef(null);

  // Fetch reCAPTCHA settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Check cache first
        if (settingsCache && Date.now() - cacheTimestamp < CACHE_TTL) {
          setSettings(settingsCache);
          setIsLoading(false);
          return;
        }

        const response = await axios.get(`${API}/site-settings/public/recaptcha`);
        settingsCache = response.data;
        cacheTimestamp = Date.now();
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch reCAPTCHA settings:', error);
        setSettings({ recaptcha_enabled: false });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Check if reCAPTCHA is enabled for this form type
  const isEnabled = settings?.recaptcha_enabled && settings?.[`recaptcha_${formType}`];
  const siteKey = settings?.recaptcha_site_key;

  // Load reCAPTCHA script
  useEffect(() => {
    if (!isEnabled || !siteKey || scriptLoaded) return;

    // Check if script is already loaded
    if (window.grecaptcha) {
      setScriptLoaded(true);
      return;
    }

    // Check if script is already loading
    const existingScript = document.querySelector('script[src*="recaptcha"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => setScriptLoaded(true));
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=explicit`;
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => console.error('Failed to load reCAPTCHA script');
    document.head.appendChild(script);
  }, [isEnabled, siteKey, scriptLoaded]);

  // Initialize reCAPTCHA widget (invisible)
  useEffect(() => {
    if (!isEnabled || !siteKey || !scriptLoaded || !window.grecaptcha) return;

    // Wait for grecaptcha to be ready
    window.grecaptcha.ready(() => {
      // Create a hidden container if it doesn't exist
      if (!containerRef.current) {
        const container = document.createElement('div');
        container.id = `recaptcha-container-${formType}`;
        container.style.display = 'none';
        document.body.appendChild(container);
        containerRef.current = container;
      }

      // Render invisible reCAPTCHA if not already rendered
      if (recaptchaId.current === null) {
        try {
          recaptchaId.current = window.grecaptcha.render(containerRef.current, {
            sitekey: siteKey,
            size: 'invisible',
            callback: () => {}, // Will be handled in executeRecaptcha
            'error-callback': () => {
              console.error('reCAPTCHA error');
            }
          });
        } catch (error) {
          // Widget might already be rendered
          console.log('reCAPTCHA widget may already be rendered:', error.message);
        }
      }
    });

    // Cleanup
    return () => {
      if (containerRef.current && containerRef.current.parentNode) {
        containerRef.current.parentNode.removeChild(containerRef.current);
        containerRef.current = null;
        recaptchaId.current = null;
      }
    };
  }, [isEnabled, siteKey, scriptLoaded, formType]);

  /**
   * Execute reCAPTCHA and get token
   * Returns token if successful, null if reCAPTCHA is disabled or fails
   */
  const executeRecaptcha = useCallback(async () => {
    if (!isEnabled) {
      return null; // reCAPTCHA disabled, return null (backend will skip verification)
    }

    if (!window.grecaptcha || recaptchaId.current === null) {
      console.warn('reCAPTCHA not ready');
      return null;
    }

    return new Promise((resolve) => {
      window.grecaptcha.ready(() => {
        try {
          window.grecaptcha.execute(recaptchaId.current);
          
          // Poll for the response (invisible reCAPTCHA doesn't have a direct callback)
          const checkResponse = () => {
            const response = window.grecaptcha.getResponse(recaptchaId.current);
            if (response) {
              resolve(response);
            } else {
              setTimeout(checkResponse, 100);
            }
          };
          
          // Set a timeout to prevent infinite waiting
          const timeout = setTimeout(() => {
            console.warn('reCAPTCHA timeout');
            resolve(null);
          }, 30000); // 30 second timeout

          const checkWithTimeout = () => {
            const response = window.grecaptcha.getResponse(recaptchaId.current);
            if (response) {
              clearTimeout(timeout);
              resolve(response);
            } else {
              setTimeout(checkWithTimeout, 100);
            }
          };
          
          checkWithTimeout();
        } catch (error) {
          console.error('reCAPTCHA execute error:', error);
          resolve(null);
        }
      });
    });
  }, [isEnabled]);

  /**
   * Reset reCAPTCHA widget (call after form submission)
   */
  const resetRecaptcha = useCallback(() => {
    if (window.grecaptcha && recaptchaId.current !== null) {
      try {
        window.grecaptcha.reset(recaptchaId.current);
      } catch (error) {
        console.error('reCAPTCHA reset error:', error);
      }
    }
  }, []);

  return {
    executeRecaptcha,
    isLoading,
    isEnabled,
    resetRecaptcha
  };
};

export default useRecaptcha;
